import type React from "react";
import { useState } from "react";
import styled from "styled-components";
import { Button } from "./Button";

const ExpandableWrapper = styled.div<{ $disabled: boolean }>`
	background-color: var(--surface);
	border-radius: 0.5rem;
	overflow: hidden;
	padding: 2rem;
`;

const ExpandableHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 1rem;
`;

const Arrow = styled.span<{ $isExpanded: boolean }>`
	display: inline-block;
	width: 0;
	height: 0;
	border-left: 5px solid transparent;
	border-right: 5px solid transparent;
	border-top: 5px solid var(--text-paragraph);
	transform: ${(props) =>
		props.$isExpanded ? "rotate(180deg)" : "rotate(0deg)"};
	transition: transform 0.3s ease;
`;

const ExpandableContent = styled.div<{ $isExpanded: boolean }>`
	max-height: ${(props) => (props.$isExpanded ? "1000px" : "0")};
	overflow: hidden;
	transition: max-height 0.3s ease-in-out;
`;

const StyledButton = styled(Button)<{ $invisible: boolean }>`
	padding: 1.25rem;
	visibility: ${(props) => (props.$invisible ? "hidden" : "visible")};
`;

interface ExpandableProps {
	header: React.ReactNode;
	children: React.ReactNode;
	disabled?: boolean;
}

export const Expandable: React.FC<ExpandableProps> = ({
	header,
	children,
	disabled = false,
}) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const toggleExpand = () => {
		if (!disabled) {
			setIsExpanded(!isExpanded);
		}
	};

	return (
		<ExpandableWrapper $disabled={disabled}>
			<ExpandableHeader>
				{header}
				<StyledButton
					onClick={toggleExpand}
					label={<Arrow $isExpanded={isExpanded} />}
					variant="outline"
					size="sm"
					ml="2rem"
					$invisible={disabled}
					disabled={disabled}
				/>
			</ExpandableHeader>
			<ExpandableContent $isExpanded={isExpanded && !disabled}>
				{children}
			</ExpandableContent>
		</ExpandableWrapper>
	);
};
